import { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams } from '@redwoodjs/router'
import type { PartHierarchyHeaderColumn } from './columns'
import localStorageApi from 'src/lib/localStorageApi'
import invariant from 'tiny-invariant';
import React from 'react';

// Use fullKey (string) for unique identification
type StoredColumnIdentifier = string;

// Single column state object
interface ColumnState {
  hidden: boolean;
  order: number;
  width: number;
}

interface ViewModeState {
  columnState: Record<StoredColumnIdentifier, ColumnState>;
}

interface HierarchyViewState {
  fullScreen?: ViewModeState;
  inline?: ViewModeState;
}

const getStoredViewState = (orgId: string, viewMemoryKey: string): HierarchyViewState => {
  const storageKey = localStorageApi.getHierarchyViewStateKey(orgId, viewMemoryKey);
  return localStorageApi.getHierarchyViewState(storageKey) || {};
};

const setStoredViewState = (orgId: string, viewMemoryKey: string, state: HierarchyViewState): void => {
  const storageKey = localStorageApi.getHierarchyViewStateKey(orgId, viewMemoryKey);
  localStorageApi.setHierarchyViewState(storageKey, state);
};

interface UseHierarchyViewStateProps {
  viewMemoryKey: string;
  allColumns: PartHierarchyHeaderColumn[];
  isFullScreen?: boolean;
}

export interface ColumnWithState extends PartHierarchyHeaderColumn {
  state: ColumnState
}

interface HierarchyViewStateResult {
  columnsWithState: ColumnWithState[];
  moveColumn: (position: 'before' | 'after', columnToMoveKey: string, targetColumnKey: string | null) => void;
  setColumnWidth: (columnKey: string, width: number) => void;
  getColumnWidth: (columnKey: string) => number | undefined;
  setColumnVisibility: (columnKey: string, isVisible: boolean) => void;
  isColumnVisible: (columnKey: string) => boolean;
}

/**
 * Creates a column state object based on the column definition and current view mode
 */
const createColumnState = ({
  column,
  index,
  isFullScreen
}: {
  column: PartHierarchyHeaderColumn;
  index: number;
  isFullScreen: boolean;
}): ColumnState => {
  const visibility = column.defaultVisibility || 'fullScreenOnly';
  const isHidden = (isFullScreen
    ? visibility === 'hidden'
    : (visibility === 'hidden' || visibility === 'fullScreenOnly'));

  return {
    hidden: isHidden,
    order: index,
    width: column.defaultWidth,
  };
};

/**
 * Initializes or updates column state for a specific view mode
 */
const initializeColumnState = ({
  columnState,
  allColumns,
  isFullScreen
}: {
  columnState: Record<StoredColumnIdentifier, ColumnState>;
  allColumns: PartHierarchyHeaderColumn[];
  isFullScreen: boolean;
}): Record<StoredColumnIdentifier, ColumnState> => {
  const newColumnState = { ...columnState };

  // Keep track of columns we've seen
  const seenColumns = new Set<string>();

  // Update existing columns and add new ones
  allColumns.forEach((col, index) => {
    seenColumns.add(col.fullKey);

    if (!newColumnState[col.fullKey]) {
      newColumnState[col.fullKey] = createColumnState({
        column: col,
        index,
        isFullScreen
      });
    }
  });

  // Optional: Remove any columns that no longer exist
  Object.keys(newColumnState).forEach(key => {
    if (!seenColumns.has(key)) {
      delete newColumnState[key];
    }
  });

  return newColumnState;
};

/**
 * Updates state for a specific mode and column
 */
const updateModeColumnState = ({
  fullState,
  mode,
  columnKey,
  updater
}: {
  fullState: HierarchyViewState;
  mode: 'fullScreen' | 'inline';
  columnKey: string;
  updater: (currentState: ColumnState) => ColumnState;
}): HierarchyViewState => {
  const newFullState = { ...fullState };
  const currentModeState = { ...newFullState[mode] } as ViewModeState;
  const newColumnState = { ...currentModeState.columnState };

  if (!newColumnState[columnKey]) return fullState;

  newColumnState[columnKey] = updater(newColumnState[columnKey]);

  // Update the state for the current mode
  newFullState[mode] = {
    ...currentModeState,
    columnState: newColumnState
  };

  return newFullState;
};

/**
 * Initializes view state for both modes if needed
 */
const initializeViewState = ({
  state,
  allColumns
}: {
  state: HierarchyViewState;
  allColumns: PartHierarchyHeaderColumn[];
}): HierarchyViewState => {
  return {
    ...state,
    fullScreen: {
      columnState: initializeColumnState({
        columnState: (state.fullScreen?.columnState || {}),
        allColumns,
        isFullScreen: true
      })
    },
    inline: {
      columnState: initializeColumnState({
        columnState: (state.inline?.columnState || {}),
        allColumns,
        isFullScreen: false
      })
    }
  };
};

/**
 * Move a column to the start of the list
 */
const moveColumnToStart = ({
  columnState,
  columnKey
}: {
  columnState: Record<StoredColumnIdentifier, ColumnState>;
  columnKey: string;
}): Record<StoredColumnIdentifier, ColumnState> => {
  console.log('moveColumnToStart called with key:', columnKey);
  const sortedEntries = Object.entries(columnState)
    .sort(([, stateA], [, stateB]) => stateA.order - stateB.order);

  console.log('sorted entries:', sortedEntries);

  if (sortedEntries.length === 0) return columnState;

  const firstEntry = sortedEntries[0];
  if (!firstEntry || !firstEntry[1]) return columnState;

  const updatedState = { ...columnState };
  const columnToMove = updatedState[columnKey];

  if (!columnToMove) {
    console.error('Column to move not found:', columnKey);
    return columnState;
  }

  updatedState[columnKey] = {
    hidden: columnToMove.hidden,
    order: firstEntry[1].order - 1,
    width: columnToMove.width
  };

  console.log('Updated state after moveColumnToStart:', updatedState);
  return updatedState;
};

/**
 * Move a column to the end of the list
 */
const moveColumnToEnd = ({
  columnState,
  columnKey
}: {
  columnState: Record<StoredColumnIdentifier, ColumnState>;
  columnKey: string;
}): Record<StoredColumnIdentifier, ColumnState> => {
  const sortedEntries = Object.entries(columnState)
    .sort(([, stateA], [, stateB]) => stateA.order - stateB.order);

  if (sortedEntries.length === 0) return columnState;

  const lastEntry = sortedEntries[sortedEntries.length - 1];
  if (!lastEntry || !lastEntry[1]) return columnState;

  const updatedState = { ...columnState };
  const columnToMove = updatedState[columnKey];

  if (!columnToMove) return columnState;

  updatedState[columnKey] = {
    hidden: columnToMove.hidden,
    order: lastEntry[1].order + 1,
    width: columnToMove.width
  };

  return updatedState;
};

/**
 * Move a column to a specific position relative to another column
 */
const moveColumnToPosition = ({
  columnState,
  columnToMoveKey,
  targetColumnKey,
  position
}: {
  columnState: Record<StoredColumnIdentifier, ColumnState>;
  columnToMoveKey: string;
  targetColumnKey: string;
  position: 'before' | 'after';
}): Record<StoredColumnIdentifier, ColumnState> => {
  const sortedEntries = Object.entries(columnState)
    .sort(([, stateA], [, stateB]) => stateA.order - stateB.order);

  // Find the target column position
  const targetIndex = sortedEntries.findIndex(([key]) => key === targetColumnKey);
  if (targetIndex === -1) return columnState;

  // Remove the column to be moved
  const filtered = sortedEntries.filter(([key]) => key !== columnToMoveKey);

  // Find where to insert
  const insertIndex = filtered.findIndex(([key]) => key === targetColumnKey);
  if (insertIndex === -1) return columnState;

  const actualInsertIndex = position === 'before' ? insertIndex : insertIndex + 1;

  // Insert the column at the new position
  const colState = columnState[columnToMoveKey];
  if (!colState) return columnState;

  filtered.splice(actualInsertIndex, 0, [columnToMoveKey, colState]);

  // Create new state with updated orders
  const updatedState = { ...columnState };
  filtered.forEach(([key], index) => {
    if (updatedState[key]) {
      updatedState[key] = {
        hidden: !!updatedState[key]?.hidden,
        order: index,
        width: updatedState[key]?.width
      };
    }
  });

  return updatedState;
};

/**
 * Hook to manage hierarchy view state in localStorage
 * @param viewMemoryKey Key to identify this specific view state
 * @param allColumns All available columns
 * @param isFullScreen Whether the view is in fullscreen mode
 * @returns State and methods to manipulate the view state
 */
export const useHierarchyViewState = ({ viewMemoryKey, allColumns, isFullScreen = false }: UseHierarchyViewStateProps): HierarchyViewStateResult => {
  const { orgId } = useParams();
  const mode = isFullScreen ? 'fullScreen' : 'inline';

  // Get stored state from localStorage and process it immediately
  const [fullState, setFullState] = useState<HierarchyViewState>(() => {
    // Get the stored state
    const storedState = getStoredViewState(orgId!, viewMemoryKey);

    // Initialize with all columns
    return initializeViewState({
      state: storedState,
      allColumns
    });
  });

  // Synchronize state with allColumns on change
  // This effect runs before render to ensure state is always in sync with allColumns
  const syncedState = useMemo(() => {
    // Don't recompute on first render since it's already handled in useState
    if (fullState[mode]?.columnState && Object.keys(fullState[mode]!.columnState).length > 0) {
      // Check if we need to synchronize (if any columns are missing from state)
      const existingColumnKeys = Object.keys(fullState[mode]!.columnState);
      const allColumnKeys = allColumns.map(col => col.fullKey);

      const hasMissingColumns = allColumnKeys.some(key => !existingColumnKeys.includes(key));
      const hasExtraColumns = existingColumnKeys.some(key => !allColumnKeys.includes(key));

      if (hasMissingColumns || hasExtraColumns) {
        console.log('Columns changed, synchronizing state');
        // Reinitialize the view state while keeping existing column settings
        return initializeViewState({
          state: fullState,
          allColumns
        });
      }
    }

    return fullState;
  }, [fullState, allColumns, mode]);

  // Apply the syncedState if it's different from the current state
  useEffect(() => {
    if (syncedState !== fullState) {
      setFullState(syncedState);
    }
  }, [syncedState, fullState]);

  // Extract columnState from the synced state
  const columnState = useMemo(() => {
    return syncedState[mode]?.columnState || {};
  }, [syncedState, mode]);

  // Calculate columnsWithState from synchronized state
  const columnsWithState = useMemo(() => {
    // This is now safe because we ensure columnState always has entries for all columns
    return allColumns
      .map(c => {
        const state = columnState[c.fullKey];
        // This invariant should never fail now because we've synchronized the state
        invariant(state, `No column state for ${c.fullKey}`);
        return { ...c, state };
      })
      .sort((a, b) => a.state.order - b.state.order);
  }, [columnState, allColumns]);

  // Save state to localStorage when it changes
  useEffect(() => {
    setStoredViewState(orgId!, viewMemoryKey, fullState);
  }, [fullState, orgId, viewMemoryKey]);

  // Function to move columns around
  const moveColumn = useCallback((position: 'before' | 'after', columnToMoveKey: string, targetColumnKey: string | null) => {
    if (!columnState[columnToMoveKey]) return;

    setFullState(currentFullState => {
      const newFullState = { ...currentFullState };
      const currentModeState = { ...newFullState[mode] } as ViewModeState;
      let newColumnState = { ...currentModeState.columnState };

      // Handle different movement cases with helper functions
      if (targetColumnKey === null) {
        if (position === 'before') {
          newColumnState = moveColumnToStart({
            columnState: newColumnState,
            columnKey: columnToMoveKey
          });
        } else {
          newColumnState = moveColumnToEnd({
            columnState: newColumnState,
            columnKey: columnToMoveKey
          });
        }
      } else {
        newColumnState = moveColumnToPosition({
          columnState: newColumnState,
          columnToMoveKey,
          targetColumnKey,
          position
        });
      }

      // Update the state for the current mode
      newFullState[mode] = {
        ...currentModeState,
        columnState: newColumnState
      };

      return newFullState;
    });
  }, [columnState, mode]);

  // Getter and setter for column width
  const setColumnWidth = useCallback((columnKey: string, width: number) => {
    setFullState(currentFullState =>
      updateModeColumnState({
        fullState: currentFullState,
        mode,
        columnKey,
        updater: currentState => ({
          hidden: !!currentState.hidden,
          order: currentState.order || 0,
          width,
        })
      })
    );
  }, [mode]);

  const getColumnWidth = useCallback((columnKey: string) => {
    return columnState[columnKey]?.width;
  }, [columnState]);

  // Getter and setter for column visibility
  const setColumnVisibility = useCallback((columnKey: string, isVisible: boolean) => {
    setFullState(currentFullState =>
      updateModeColumnState({
        fullState: currentFullState,
        mode,
        columnKey,
        updater: currentState => ({
          hidden: !isVisible,
          order: currentState.order || 0,
          width: currentState.width,
        })
      })
    );
  }, [mode]);

  const isColumnVisible = useCallback((columnKey: string) => {
    return columnState[columnKey] ? !columnState[columnKey].hidden : false;
  }, [columnState]);

  return {
    columnsWithState,
    moveColumn,
    setColumnWidth,
    getColumnWidth,
    setColumnVisibility,
    isColumnVisible,
  };
};